<template>
  <div class="header-wrapper">
    <div class="header">
      <div class="flex-grow-1">
        <div class="d-flex align-items-center">
          <div class="menu-icon mr-2" @click="showSideMenu = !showSideMenu">
            <BarsIcon v-if="!showSideMenu" />
            <XMarkIcon v-else />
          </div>
          <div class="logo">
            <img src="@/assets/icon.svg" alt="Logo" />
          </div>
        </div>
      </div>
      <div v-if="user?.['https://app.etainabl.com/companyId']" class="user">
        <UserBox :key="user['https://app.etainabl.com/companyId']" />
      </div>
    </div>
    <div v-if="queueActivity" class="header-alert alert alert-info">Submitting readings...</div>
  </div>

  <div class="side-menu" :class="{ show: showSideMenu }">
    <div v-if="isAuthenticated" class="side-menu-item" @click="onClickMenuItem('select')">
      <PlusIcon />
      <div>Submit a new reading</div>
    </div>
    <div v-if="isAuthenticated" class="side-menu-item" @click="onClickMenuItem('queue')">
      <HourglassClockIcon />
      <div>Pending readings</div>
    </div>
    <div v-if="isAuthenticated && canSwitchCompany" class="side-menu-item" @click="onClickSwitchCompany">
      <ArrowsRepeatIcon />
      <div>Switch Company</div>
    </div>
    <div class="side-menu-item mt-5">
      <RightFromBracketIcon />
      <div @click.prevent="onClickLogout">Logout</div>
    </div>
  </div>

  <CompanyScopedLoader :key="user?.['https://app.etainabl.com/companyId']" />

  <div v-if="loading" class="content">
    <div class="spinner-wrapper">
      <SpinnerLogo>Logging in...</SpinnerLogo>
    </div>
  </div>

  <div v-else :class="{ 'is-alert': queueActivity }">
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { Network } from '@capacitor/network';
import { useAuth0 } from '@auth0/auth0-vue';
import localforage from 'localforage';
import { ref, onBeforeMount, onBeforeUnmount, toRaw, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import * as Sentry from '@sentry/vue';

import UserBox from '@/components/UserBox.vue';
import CompanyScopedLoader from '@/components/CompanyScopedLoader.vue';
import BarsIcon from '@/components/icons/BarsIcon.vue';
import HourglassClockIcon from '@/components/icons/HourglassClockIcon.vue';
import PlusIcon from '@/components/icons/PlusIcon.vue';
import RightFromBracketIcon from '@/components/icons/RightFromBracketIcon.vue';
import ArrowsRepeatIcon from '@/components/icons/ArrowsRepeatIcon.vue';
import SpinnerLogo from '@/components/SpinnerLogo.vue';
import XMarkIcon from '@/components/icons/XMarkIcon.vue';

import { useStore as useQueueStore } from '@/stores/queue';
import { useStore as useUtilStore } from '@/stores/util';
import { useStore as useAssetStore } from '@/stores/asset';
import { useStore as useAccountStore } from '@/stores/account';
import { useStore as useCompanyStore } from '@/stores/company';
import { useStore as useAuthStore } from '@/stores/auth';
import { arrivedFromLogin, flush, maybeFireSessionStart } from '@/lib/usage';

const { isAuthenticated, user, logout } = useAuth0();

const utilStore = useUtilStore();
const queueStore = useQueueStore();
const assetStore = useAssetStore();
const accountStore = useAccountStore();
const companyStore = useCompanyStore();
const authStore = useAuthStore();
const $router = useRouter();

const showSideMenu = ref(false);
const loading = ref(true);

const canSwitchCompany = computed(() => companyStore.globalCompanies.length > 1);

// Attach the signed-in user to telemetry so events (including the memory
// diagnostics) are attributable to a specific person/device — mirrors the main
// UI's Sentry.setUser on login / clear on logout. The id is enough for triage,
// so email and name stay out of Better Stack.
watch(
  user,
  (u) => {
    if (u) {
      Sentry.setUser({
        id: u.sub,
        companyId: u['https://app.etainabl.com/companyId']
      });
    } else {
      Sentry.setUser(null);
    }
  },
  { immediate: true }
);

watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated)
      maybeFireSessionStart({ trigger: arrivedFromLogin ? 'login' : 'reactivation' });
  },
  { immediate: true }
);

onBeforeMount(async () => {
  Network.addListener('networkStatusChange', (status) => {
    utilStore.isOffline = !status.connected;

    if (status.connected) {
      queueStore.processPendingQueueItems();
      flush();
    }
  });

  // Sync store offline status with actual network status
  utilStore.isOffline = !(await Network.getStatus()).connected;

  loading.value = false;
});

onBeforeUnmount(() => {
  Network.removeAllListeners();
});

const onClickMenuItem = (route: string) => {
  showSideMenu.value = false;
  $router.push({ name: route });
};

const onClickSwitchCompany = () => {
  showSideMenu.value = false;
  $router.push({ name: 'switch-company' });
};

const onClickLogout = async () => {
  Sentry.setUser(null);
  flush();
  await authStore.endServerSession();
  await logout();
  showSideMenu.value = false;
  $router.push({ name: 'login' });
};

const queueActivity = queueStore.submittingQueueItems.length;

// Build a persistence-safe snapshot. Only unsent (pending) items keep their
// base64 photo — they still need it to retry the upload. Submitted/errored
// items drop the image (it's in S3, reachable via readingId), which is what
// stops IndexedDB from growing with every reading.
const serializeQueueForPersist = (queue: typeof queueStore.queue) =>
  queue.map((item) => {
    const raw = toRaw(item);
    // Drop the base64 photo only when it's safely in S3 — i.e. submitted AND it
    // has a photoS3Key. Keep it for pending/error items (still to be sent) and
    // for submitted items whose upload failed (no S3 copy — the base64 is the
    // only copy and would otherwise be lost).
    if (raw.status !== 'submitted' || !raw.photoS3Key) return raw;
    return {
      ...raw,
      state: { ...raw.state, photoData: { dataUrl: '', format: '' } }
    };
  });

// Persist on every mutation, synchronously (flush: 'sync'). The serialized
// snapshot is small (submitted-item photos are stripped — see
// serializeQueueForPersist), so syncing every change is cheap, and it kicks off
// the write *during* the mutation rather than deferring it to a later tick —
// minimising the window where a tab kill could leave a stale `pending` snapshot
// on disk and resubmit the reading (duplicate) on next launch.
queueStore.$subscribe(
  (mutation, state) => {
    if (!user.value) return;
    const companyId = user.value['https://app.etainabl.com/companyId'];
    localforage.setItem(
      `queue-${companyId}`,
      JSON.stringify({ queue: serializeQueueForPersist(state.queue) })
    );
  },
  { flush: 'sync' }
);
</script>
